import { useCallback, useEffect, useState } from "react";
import Button from "../../../../shared/Button";
import Modal from "../../../../shared/Modal";
import LegCreate from ".";
import { useAuth } from "../../../../hooks/useAuth";
import { useConfig } from "../../../../hooks/useConfig";
import { parse, stringify } from "flatted";
import { toast } from "react-toastify";
import useEmblaCarousel from "embla-carousel-react";
import { formatDate, updateTime } from "../../../../utils";
import useForm from "../../../../hooks/useForm";
import {
  useAddLegMutation,
  useUpdateLegMutation,
} from "../../../../redux/services/legs/legsApi";
import "./style.css";
import {
  isLegFleetEquipmentEnabled,
  normalizeLegEquipmentPayload,
} from "../../../../utils/legFleetEquipment";
import {
  buildLegEditSnapshot,
  resolveLegDriverId,
  resolveLegEditSaveStatus,
} from "../../../../utils/legUpdateStatus";
import {
  buildOptionalLegShippingFields,
  isManifestV2ShippingEnabled,
} from "../../../../utils/legShipping";
import {
  getDriverComplianceWarningsFromResponse,
  showDriverComplianceWarnings,
} from "../../../../utils/driver-compliance-toast";

export const LegCreateModal = ({
  visible,
  setVisible,
  form,
  job,
  onCancel,
  deferCreate = false,
  onDeferCreate,
  pendingEditId,
  onDeferUpdate,
  inline = false,
}) => {
  // Get current user data
  const { user } = useAuth();
  const { configs } = useConfig();
  const legFleetEquipmentEnabled = isLegFleetEquipmentEnabled(configs);

  // Main modal title
  const [modalTitle, setModalTitle] = useState("Add");
  // Submit button title
  const [submitButton, setSubmitButton] = useState("Submit");
  // Cancel button title
  const [cancelButton, setCancelButton] = useState("Cancel");

  // Handoff form hook
  const handoffForm = useForm();

  // Add leg hook
  const [addLeg, { isLoading: addingLeg }] = useAddLegMutation();
  // Update leg hook
  const [updateLeg, { isLoading: updatingLeg }] = useUpdateLegMutation();

  // Carousel hook
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    watchDrag: false,
  });

  // Handle carousel back
  const scrollPrev = useCallback(() => {
    setSubmitButton("Next");
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  // Handle carousel next
  const scrollNext = useCallback(() => {
    setSubmitButton("Submit");
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Close job leg modal
  function closeModal() {
    form.clearForm();
    onCancel(false);
  }

  // ✅ FIXED: Updated getApptDate function using proper utilities
  function getApptDate(date, time) {
    if (!date) return null;

    try {
      const apptDate = new Date(date);
      if (isNaN(apptDate.getTime())) return null;

      if (time) {
        // ✅ Use the proper updateTime utility instead of manual construction
        const updatedDate = updateTime(apptDate, time);
        return updatedDate;
      }
      return apptDate;
    } catch (e) {
      console.error("Error creating appointment date:", e);
      return null;
    }
  }

  // Handle leg update
  async function handleUpdate(e) {
    let updateData = {
      parent: user.parentCompany,
      data: e,
    };

    if (e.column) {
      updateData = e.column.editable.update
        ? e.column.editable.update({ user, ...e })
        : {
            ...updateData,
            legId: e.row.original._id,
            data: e.data,
          };
    } else {
      const nextDriverId = resolveLegDriverId(e.driver);
      const nextAppointmentDate = getApptDate(e.appointmentDate, e.appointmentTime);
      const editSnapshot = e._editSnapshot || buildLegEditSnapshot(e);

      const nextStatus = resolveLegEditSaveStatus({
        currentStatus: editSnapshot.status ?? e.status,
        nextDriverId,
        nextAppointmentDate,
        originalDriverId: editSnapshot.driverId,
        originalAppointmentDate: editSnapshot.appointmentDate,
      });

      const legEquipment = normalizeLegEquipmentPayload(e, {
        enabled: legFleetEquipmentEnabled,
      });
      const shippingFields = buildOptionalLegShippingFields(e, configs);

      updateData = {
        parent: user.parentCompany,
        legId: e._id,
        jobId: job._id,
        data: {
          ...e,
          powerUnit: legEquipment.powerUnit,
          trailer: legEquipment.trailer,
          // If admin clears driver, explicitly set null (unassign).
          driver: nextDriverId,
          client: job.client?._id,
          route: {
            origin: e.origin?._id,
            destination: e.destination?._id,
            mileage: Number(e.totalMiles),
            customMileage:
              e.customMileage === null ||
              e.customMileage === undefined ||
              e.customMileage === ""
                ? null
                : Number(e.customMileage),
            _id: form.form.route?._id,
          },
          mileageUsed:
            e.mileageUsed !== undefined &&
            e.mileageUsed !== null &&
            e.mileageUsed !== ""
              ? Number(e.mileageUsed)
              : Number(e.totalMiles),
          mileageSelectionType:
            e.mileageSelectionType ||
            (e.useCustomMileage ? "billedMileage" : "defaultMileage"),
          ...(e.billedMiles != null ? { billedMiles: e.billedMiles } : {}),
          ...(e.useCustomMileage !== undefined
            ? { useCustomMileage: !!e.useCustomMileage }
            : {}),
          appointmentDate: nextAppointmentDate,
          // appointment: { date: e.appointmentDate, time: e.appointmentTime },
          waitTime: {
            total: e.totalWaitTime,
            start: e.waitStart,
            end: e.waitEnd,
          },
          ...shippingFields,
          ...(nextStatus ? { status: nextStatus } : {}),
        },
      };
      if (!isManifestV2ShippingEnabled(configs)) {
        delete updateData.data.shipping;
        delete updateData.data.complianceProfile;
      }
      delete updateData.data._editSnapshot;
      delete updateData.origin;
      delete updateData.destination;
      // delete updateData.appointmentDate;
      // delete updateData.appointment;
      delete updateData.author;
      delete updateData.dateCreated;
      delete updateData.dateUpdated;
      delete updateData.parentCompany;
      delete updateData.payoutId;
      delete updateData.status;
    }
    const dataString = stringify(updateData);
    const dataToSend = parse(dataString);

    let res = await updateLeg(dataToSend).unwrap();

    showDriverComplianceWarnings(
      getDriverComplianceWarningsFromResponse(res),
      { toast }
    );

    if (res.status === "success") {
      closeModal();
    }
  }

  // Reset handoff data
  function resetHandoff() {
    setSubmitButton("Submit");
    handoffForm.clearForm();
  }

  // Toggle handoff from main form
  function toggleHandoff(e) {
    // Set hasHandoff
    form.updateForm(e);

    if (e.value) {
      // Set submit button title if handoff is checked true
      setSubmitButton("Next");
    } else {
      // Reset handoff if toggled off
      resetHandoff();
      return;
    }
  }

  // Handle next if handoff is selected
  function handleNext() {
    // Set main button to submit
    setSubmitButton("Submit");

    // Set handoffs appointment date
    const apptDate = formatDate(form.job?.transactionDate || null, null, true);

    // Set inital handoff form data
    handoffForm.setFormInit({
      job,
      useLegFlatRate: false,
      origin: form.form.destination,
      orderNumber: form.form.orderNumber,
      loadNumber: form.form.loadNumber,
      loadType: form.form.loadType,
      destination: null,
      weight: form.form.weight,
      driver: {},
      appointmentTime: null,
      appointmentDate: apptDate,
      waitEnd: null,
      waitStart: null,
      waitTime: false,
      totalWaitTime: 0,
      baseCharge: 0,
      fuelSurcharge: 0,
      otherCharges: 0,
      dropCharge: 0,
      useCustomFuelSurcharge: false,
      useCustomBaseCharge: false,
      useCustomDropCharge: false,
      useCustomOtherCharges: false,
      totalMiles: 0,
      totalCharges: 0,
      hasHandoff: null,
      isHandoff: true,
    });

    // Handle carousel next
    scrollNext();
  }

  async function saveHandoff(handoffParent) {
    const getLegStatusForCreate = ({ driverId, appointmentDate }) => {
      // Admin create rule:
      // - If appointment date/time is set -> scheduled
      // - Else if driver is set -> assigned
      // - Else -> pending
      if (appointmentDate) return "scheduled";
      if (driverId) return "assigned";
      return "pending";
    };

    const handoffData = {
      ...handoffForm.form,
      pickupDate: handoffForm.form.job.transactionDate,
      driver: handoffForm.form.driver?._id,
      destination: handoffForm.form.destination?._id,
      origin: handoffForm.form.origin?._id,
      jobId: handoffForm.form.job._id,
      handoffParent,
      // New legs should reflect admin inputs
      status: getLegStatusForCreate({
        driverId: handoffForm.form.driver?._id,
        appointmentDate: getApptDate(
          handoffForm.form.appointmentDate,
          handoffForm.form.appointmentTime
        ),
      }),
      active: false,
    };

    delete handoffData.job;

    if (deferCreate) {
      onDeferCreate &&
        onDeferCreate({
          type: "handoff",
          args: {
            data: handoffData,
            client: job.client._id,
            jobId: handoffData.jobId,
            addToJob: true,
          },
        });
      toast("Handoff leg queued (will save when job is saved)", {
        type: "info",
        toastId: "leg-handoff-deferred",
      });
      return { status: "success", data: { leg: { _id: null } } };
    }

    const res = await addLeg({
      data: handoffData,
      client: job.client._id,
      jobId: handoffData.jobId,
      addToJob: true,
    }).unwrap();

    // Main leg toast
    toast("Successfully created handoff leg", {
      type: res.status,
      toastId: "leg-handoff-" + res.data.leg.orderNumber + "-added",
    });

    return res;
  }

  function postSaveCleanUp() {
    closeModal();
    setModalTitle("Add");
    resetHandoff();
  }

  // Save form with no handoff
  async function save() {
    const getLegStatusForCreate = ({ driverId, appointmentDate }) => {
      if (appointmentDate) return "scheduled";
      if (driverId) return "assigned";
      return "pending";
    };

    const appointmentDate = getApptDate(
      form.form.appointmentDate,
      form.form.appointmentTime
    );
    const driverId = form.form.driver?._id || null;

    // Main leg data
    const legEquipment = normalizeLegEquipmentPayload(form.form, {
      enabled: legFleetEquipmentEnabled,
    });
    const shippingFields = buildOptionalLegShippingFields(form.form, configs);

    const data = {
      ...form.form,
      pickupDate: form.form.job.transactionDate,
      driver: driverId,
      powerUnit: legEquipment.powerUnit,
      trailer: legEquipment.trailer,
      destination: form.form.destination?._id,
      origin: form.form.origin?._id,
      jobId: form.form.job._id,
      appointmentDate,
      // Ensure loadNumber is preserved from the job
      loadNumber: form.form.loadNumber || form.form.job?.loadNumber,
      // New legs should reflect admin inputs
      status: getLegStatusForCreate({ driverId, appointmentDate }),
      active: false,
      ...shippingFields,
    };

    // Remove job before sending leg to create
    delete data.job;
    if (!isManifestV2ShippingEnabled(configs)) {
      delete data.shipping;
      delete data.complianceProfile;
    }

    if (deferCreate) {
      // In deferred mode, queue the leg(s) to be created after the job is saved.
      // If the user configured a handoff, we queue the handoff payload too and
      // the caller will wire up `handoffParent` after the main leg is created.
      const pendingId =
        pendingEditId ||
        `pending-leg-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
      const pending = {
        id: pendingId,
        label: `Leg ${data.orderNumber || data.loadNumber || "New"}`,
        mainArgs: {
          data,
          client: job.client._id,
          jobId: data.jobId,
          addToJob: true,
        },
        // Keep rich objects for UI so draft rows can render using the same table layout.
        display: {
          origin: form.form.origin || null,
          destination: form.form.destination || null,
          appointmentDate: data.appointmentDate || null,
          driver: form.form.driver || null,
        },
      };

      if (data.hasHandoff) {
        const handoffAppointmentDate = getApptDate(
          handoffForm.form.appointmentDate,
          handoffForm.form.appointmentTime
        );
        const handoffDriverId = handoffForm.form.driver?._id || null;
        const handoffData = {
          ...handoffForm.form,
          pickupDate: handoffForm.form.job.transactionDate,
          driver: handoffDriverId,
          destination: handoffForm.form.destination?._id,
          origin: handoffForm.form.origin?._id,
          jobId: handoffForm.form.job._id,
          // New legs should reflect admin inputs
          status: getLegStatusForCreate({
            driverId: handoffDriverId,
            appointmentDate: handoffAppointmentDate,
          }),
          active: false,
        };
        delete handoffData.job;
        pending.handoffArgs = {
          data: handoffData,
          client: job.client._id,
          jobId: handoffData.jobId,
          addToJob: true,
        };
        pending.handoffDisplay = {
          origin: handoffForm.form.origin || null,
          destination: handoffForm.form.destination || null,
          appointmentDate: getApptDate(
            handoffForm.form.appointmentDate,
            handoffForm.form.appointmentTime
          ),
        };
      }

      if (pendingEditId) {
        onDeferUpdate && onDeferUpdate(pending);
      } else {
        onDeferCreate && onDeferCreate(pending);
      }

      toast(pendingEditId ? "Draft leg updated" : "Leg queued (will save when job is saved)", {
        type: "info",
        toastId: pendingEditId ? "leg-draft-updated" : "leg-deferred",
      });
      postSaveCleanUp();
      return;
    }

    // Create main leg
    const res = await addLeg({
      data,
      client: job.client._id,
      jobId: data.jobId,
      addToJob: true,
    }).unwrap();

    showDriverComplianceWarnings(
      getDriverComplianceWarningsFromResponse(res),
      { toast }
    );

    // Main leg toast
    toast("Successfully created leg", {
      type: res.status,
      toastId: "leg-" + res.data.leg.orderNumber + "-added",
    });

    if (data.hasHandoff) {
      const handoffSaved = await saveHandoff(res.data.leg._id);

      if (res.status === "success" && handoffSaved.status === "success") {
        postSaveCleanUp();
      }
      return;
    }

    if (res.status === "success") {
      postSaveCleanUp();
    }
  }

  // Main modal button handler
  function handleSubmit() {
    // If main form has handoff handle next form
    if (form.form.hasHandoff && emblaApi?.canScrollNext()) {
      handleNext();
      return;
    }

    // If not handoff save
    if (!form.form.edit) {
      return save();
    }

    // Handle update
    return handleUpdate(form.form);
  }

  // Handle cancel button
  function handleCancel() {
    closeModal();
    resetHandoff();
  }

  // Main form title
  const title = form.form.orderNumber || (form.form.edit ? "Empty leg" : "New Leg");

  // Main form title
  const handoffTitle = handoffForm.form.orderNumber || "New Handoff Leg";

  // Form footer
  const footer = (
    <div>
      {/* Cancel button */}
      <Button type="alternative" className="mr-2" onClick={handleCancel}>
        {cancelButton}
      </Button>
      {/* Back button */}
      {submitButton === "Submit" && handoffForm.form.isHandoff && (
        <Button type="alternative" className="mr-2" onClick={scrollPrev}>
          Back
        </Button>
      )}
      {/* Main modal button */}
      <Button
        id="submit-leg"
        loading={addingLeg || updatingLeg}
        disabled={addingLeg || updatingLeg || !form.isValid}
        onClick={handleSubmit}
      >
        {submitButton}
      </Button>
    </div>
  );

  const content = (
    <div className="embla" ref={emblaRef}>
      <div className="embla__container">
        <div className="embla__slide">
          <LegCreate
            {...form}
            toggleHandoff={toggleHandoff}
            type={modalTitle}
            job={job}
            client={job?.client}
            jobCreateLayout={inline}
          />
        </div>
        {form.form.hasHandoff && (
          <div className="embla__slide">
            <LegCreate
              {...handoffForm}
              type={handoffTitle}
              job={job}
              client={job?.client}
              jobCreateLayout={inline}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (inline) {
    if (!visible) return null;
    return (
      <div className="mb-3 rounded-lg border border-gray-200 bg-white">
        <div className="border-b px-4 py-2 text-sm font-semibold text-gray-800">
          {title}
        </div>
        <div className="p-3">{content}</div>
        <div className="flex justify-end border-t px-3 py-2">{footer}</div>
      </div>
    );
  }

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      onClose={closeModal}
      title={title}
      footer={footer}
      content={content}
      size="xl"
      footerPosition="right"
      contentWrapperClass="p-3 lg:px-6 space-y-6"
    />
  );
};
